import { Checkbox } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import { useState } from "react";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import {
  type OversightContractRow,
  TYPE_LABELS,
} from "./oversight-contracts-editable-panel";

export type Building = {
  id: number;
  shortName: string;
  name: string;
};

export type OversightBuildingsGridProps = {
  contracts: OversightContractRow[];
  buildings: Building[];
  onSuccess: () => Promise<void>;
};

/**
 * Screen 909 main grid — the oversight-contract building scope, laid
 * out like the 910 intervention matrix: contracts as rows, buildings as
 * columns (variable count, so this grid may scroll horizontally with frozen
 * row/column headers), a checkbox at each intersection and a "tout cocher"
 * master checkbox at the end of each row.
 */
export function OversightBuildingsGrid({
  contracts,
  buildings,
  onSuccess,
}: OversightBuildingsGridProps) {
  const [busy, setBusy] = useState(false);

  const addBuilding = async (contractId: number, buildingId: number) => {
    const { error } = await apiClient.POST(
      "/api/admin/contracts/{id}/batiments",
      { params: { path: { id: contractId } }, body: { buildingId } },
    );
    return error;
  };

  const removeBuilding = async (contractId: number, associationId: number) => {
    const { error } = await apiClient.DELETE(
      "/api/admin/contracts/{id}/batiments/{batimentId}",
      { params: { path: { id: contractId, batimentId: associationId } } },
    );
    return error;
  };

  const toggleBuilding = async (
    contract: OversightContractRow,
    buildingId: number,
    checked: boolean,
  ) => {
    setBusy(true);
    try {
      const association = contract.batiments.find(
        (b) => b.buildingId === buildingId,
      );
      const error = checked
        ? await addBuilding(contract.id, buildingId)
        : isNotNil(association)
          ? await removeBuilding(contract.id, association.id)
          : undefined;
      if (error) {
        toaster.error({
          title: "Erreur",
          description: error.error || "Échec de la mise à jour du périmètre",
        });
        return;
      }
      await onSuccess();
    } finally {
      setBusy(false);
    }
  };

  const toggleAll = async (
    contract: OversightContractRow,
    checked: boolean,
  ) => {
    setBusy(true);
    try {
      let failed = false;
      for (const building of buildings) {
        const association = contract.batiments.find(
          (b) => b.buildingId === building.id,
        );
        if (checked && isNil(association)) {
          const error = await addBuilding(contract.id, building.id);
          if (error) failed = true;
        } else if (!checked && isNotNil(association)) {
          const error = await removeBuilding(contract.id, association.id);
          if (error) failed = true;
        }
      }
      if (failed) {
        toaster.error({
          title: "Erreur",
          description: "Échec de la mise à jour du périmètre",
        });
      }
      await onSuccess();
    } finally {
      setBusy(false);
    }
  };

  if (buildings.length === 0) {
    return (
      <div className="flex min-h-[120px] items-center justify-center rounded-lg border border-dashed border-zinc-300 text-sm text-zinc-500 dark:border-zinc-700">
        Aucun bâtiment sur ce chantier. Définissez d'abord le découpage
        géographique.
      </div>
    );
  }

  if (contracts.length === 0) {
    return (
      <div className="flex min-h-[120px] items-center justify-center rounded-lg border border-dashed border-zinc-300 text-sm text-zinc-500 dark:border-zinc-700">
        Aucun contrat donneur d'ordre. Ajoutez-en depuis le panneau Contrats.
      </div>
    );
  }

  return (
    <div className="isolate h-full overflow-auto overscroll-none rounded-lg border border-zinc-200 dark:border-zinc-800">
      <table className="w-full table-fixed border-separate border-spacing-0 text-xs">
        <colgroup>
          <col className="w-56" />
          {buildings.map((building) => (
            <col key={building.id} className="w-16" />
          ))}
          <col className="w-16" />
        </colgroup>
        <thead>
          <tr>
            <th className="sticky left-0 top-0 z-30 h-9 truncate border-b border-r border-zinc-200 bg-zinc-50 p-2 text-left dark:border-zinc-800 dark:bg-zinc-900">
              Contrat
            </th>
            {buildings.map((building) => (
              <th
                key={building.id}
                className="sticky top-0 z-20 border-b border-l border-zinc-200 bg-zinc-50 p-1 text-center font-normal text-zinc-500 dark:border-zinc-800 dark:bg-zinc-900"
                title={building.name}
              >
                <div className="mx-auto flex h-24 items-start justify-center">
                  <span
                    className="whitespace-nowrap"
                    style={{
                      writingMode: "vertical-rl",
                      maxHeight: "6rem",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {building.shortName || building.name}
                  </span>
                </div>
              </th>
            ))}
            <th
              className="sticky top-0 z-20 border-b border-l border-zinc-200 bg-zinc-50 p-1 text-center font-medium text-zinc-500 dark:border-zinc-800 dark:bg-zinc-900"
              title="Tout cocher"
            >
              <div className="mx-auto flex h-24 items-start justify-center">
                <span
                  className="whitespace-nowrap"
                  style={{ writingMode: "vertical-rl" }}
                >
                  Tout
                </span>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {contracts.map((contract) => {
            const label = isNotNil(contract.shortName)
              ? `${contract.performer.name} (${contract.shortName})`
              : contract.performer.name;
            const allChecked = buildings.every((b) =>
              contract.batiments.some((cb) => cb.buildingId === b.id),
            );
            return (
              <tr key={contract.id}>
                <th
                  className="sticky left-0 z-10 h-9 truncate border-b border-r border-zinc-200 bg-white p-2 text-left font-normal text-zinc-700 dark:border-zinc-800 dark:bg-zinc-950 dark:text-zinc-300"
                  title={`${TYPE_LABELS[contract.type]} — ${label}`}
                >
                  <span className="mr-1 text-zinc-400">
                    {TYPE_LABELS[contract.type]} —
                  </span>
                  {label}
                </th>
                {buildings.map((building) => (
                  <td
                    key={building.id}
                    className="h-9 border-b border-l border-zinc-100 p-1 text-center dark:border-zinc-800"
                  >
                    <Checkbox
                      checked={contract.batiments.some(
                        (cb) => cb.buildingId === building.id,
                      )}
                      disabled={busy}
                      onCheckedChange={(details) =>
                        toggleBuilding(
                          contract,
                          building.id,
                          details.checked === true,
                        )
                      }
                    />
                  </td>
                ))}
                <td className="h-9 border-b border-l border-zinc-200 bg-zinc-50/50 p-1 text-center dark:border-zinc-800 dark:bg-zinc-900/50">
                  <Checkbox
                    checked={allChecked}
                    disabled={busy}
                    onCheckedChange={(details) =>
                      toggleAll(contract, details.checked === true)
                    }
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
